import * as localDate from "localDate";
import * as _ from "lodash";
import * as moment from "moment";


class JSONMapper<T> {
	constructor(mappers: JSONMapper.JSONMapperLike<T>) {
		this._fromJSON = mappers.fromJSON || _.identity;
		this._toJSON = mappers.toJSON || _.identity;
	}

	static create<T>(mappers: JSONMapper.JSONMapperLike<T>): JSONMapper<T> {
		if (mappers instanceof JSONMapper)
			return mappers;
		return new JSONMapper(mappers);
	}


	private _fromJSON: (json: any) => T;
	private _toJSON: (value: T) => any;


	toJSON(data: T): any {
		if (data == null)
			return null;
		return this._toJSON(data);
	}

	fromJSON(json: any): T {
		if (json == null)
			return null;
		return this._fromJSON(json);
	}


	compose<U>(other: JSONMapper.JSONMapperLike<U>): JSONMapper<T> {
		return new JSONMapper<T>({
			fromJSON: _.compose(this.fromJSON, other.fromJSON) as JSONMapper.FromJSONFn<T>,
			toJSON: _.compose(other.toJSON, this.toJSON) as JSONMapper.ToJSONFn<T>,
		});
	}

	andThen<U>(other: JSONMapper.JSONMapperLike<U>): JSONMapper<U> {
		return new JSONMapper<U>({
			fromJSON: _.compose(other.fromJSON, this.fromJSON) as JSONMapper.FromJSONFn<U>,
			toJSON: _.compose(this.toJSON, other.toJSON) as JSONMapper.ToJSONFn<U>,
		});
	}


	static identity = new JSONMapper({});

	static uuid = new JSONMapper({
		fromJSON: _.method("toLowerCase"),
		toJSON: _.method("toLowerCase"),
	});

	static timestampFromISO = new JSONMapper({
		fromJSON: moment.utc,
		toJSON: _.method("toJSON"),
	});

	static localTimestampFromISO = new JSONMapper({
		fromJSON: moment,
		toJSON: _.method("toJSON"),
	});

	static localTimestampFromISOWithOutMilliseconds = new JSONMapper({
		fromJSON: moment,
		toJSON(value) {
			return moment(value).format('YYYY-MM-DDTHH:mm:ss[Z]'); // This formats the moment object without milliseconds;
		},
	});

	static localDateFromISO = new JSONMapper({
		fromJSON: localDate,
		toJSON(value) {
			return moment(value).format("YYYY-MM-DD");
		},
	});

	static durationFromSeconds = new JSONMapper({
		fromJSON: _.partialRight(moment.duration, "seconds"),
		toJSON: _.method("asSeconds"),
	});

	static arrayOf<T>(elementMapper: JSONMapper<T>): JSONMapper<Array<T>> {
		return new JSONMapper<Array<T>>({
			fromJSON(jsonData) {
				return _.map(jsonData, json => elementMapper.fromJSON(json));
			},
			toJSON(data) {
				return _.map(data, elem => elementMapper.toJSON(elem));
			},
		});
	}

	static arrayOfClass<T>(constructor: {fromJSON: JSONMapper.FromJSONFn<T>}): JSONMapper<Array<T>> {
		return JSONMapper.arrayOf(JSONMapper.classByMethods(constructor));
	}

	static objectFromArrayOf<T>(elementMapper: JSONMapper<T>, keyFn: (obj: T) => string): JSONMapper<_.Dictionary<T>> {
		return JSONMapper.arrayOf(elementMapper).andThen({
			fromJSON(array: Array<T>) {
				return _.indexBy(array, keyFn);
			},
			toJSON(object: _.Dictionary<T>) {
				return _.values(object);
			},
		});
	}

	static object(objectMapping: Array<string> | JSONMapper.RawObjectMapping): JSONMapper<any>;
	static object<T>(objectMapping: Array<string> | JSONMapper.RawObjectMapping, construct: (data: any) => T): JSONMapper<T>;
	static object(objectMapping: Array<string> | JSONMapper.RawObjectMapping, construct: (data: any) => any = data => data): JSONMapper<any> {
		objectMapping = JSONMapper._canonicalObjectMapping(objectMapping);

		return new JSONMapper({
			fromJSON(jsonData) {
				let data = _.mapValues(objectMapping, function(fieldMapping) {
					let jsonValue = _.get(jsonData, fieldMapping.jsonKey);
					return fieldMapping.mapper.fromJSON(jsonValue);
				});
				return construct(data);
			},

			toJSON(data: any) {
				let jsonData = {};
				_.forEach(objectMapping, function(fieldMapping, key) {
					let value = data[key];
					let jsonValue = fieldMapping.mapper.toJSON(value);
					_.set(jsonData, fieldMapping.jsonKey, jsonValue);
				});
				return jsonData;
			},
		});
	}

	static classByMethods<T>(constructor: {fromJSON: JSONMapper.FromJSONFn<T>}): JSONMapper<T> {
		return new JSONMapper<T>({
			fromJSON: json => constructor.fromJSON(json),
			toJSON: _.method("toJSON"),
		});
	}



	extendClass(constructor: any) {
		// Can't use an arrow function here
		// tslint:disable-next-line:no-this-assignment
		let jsonMapper = this;

		constructor.prototype.toJSON = function() {
			return jsonMapper.toJSON(this);
		};

		constructor.fromJSON = function(json: any) {
			let data = jsonMapper.fromJSON(json);
			return new constructor(data);
		};
	}

	static extendClass(constructor: any, objectMapping: Array<string> | JSONMapper.RawObjectMapping) {
		JSONMapper.object(objectMapping).extendClass(constructor);
	}

	static _canonicalObjectMapping(rawObjectMapping: Array<string> | JSONMapper.RawObjectMapping): JSONMapper.ObjectMapping {
		if (_.isArray(rawObjectMapping)) {
			// The raw mapping is a simple array of field names: ['foo', 'bar', 'baz']
			let objectMapping: JSONMapper.ObjectMapping = {};
			_.forEach(rawObjectMapping, function(key) {
				objectMapping[key] = {
					jsonKey: key,
					mapper: JSONMapper.identity,
				};
			});
			return objectMapping;
		}
		else {
			// The raw mapping is an object where the keys are field names of the result object. The values are the mappings
			// for each key, which could be give in a few different formats: {foo: ???, bar: ???, baz: ???}
			// NOTE: _.mapValues only covers own properties, and we need to include the prototype chain
			let objectMapping: JSONMapper.ObjectMapping = {};
			_.forIn(rawObjectMapping, function (fieldMapping, key) {
				if (fieldMapping === true) {
					// The field mapping is the value true, which simply means to map this field unchanged
					objectMapping[key] = {
						jsonKey: key,
						mapper: JSONMapper.identity,
					};
				}
				else if (typeof fieldMapping === "string") {
					// The field mapping is a string indicating the name of the field in the source JSON
					objectMapping[key] = {
						jsonKey: fieldMapping,
						mapper: JSONMapper.identity,
					};
				}
				else if (JSONMapper.isJSONMapperLike(fieldMapping)) {
					// The field mapping is an object with fromJSON and toJSON functions
					objectMapping[key] = {
						jsonKey: key,
						mapper: JSONMapper.create(fieldMapping),
					};
				}
				else {
					// The field mapping is an object with two optional properties:
					//  - jsonKey: The name of the field in the source JSON (defaults to the result object field name)
					//  - mapper: An object with fromJSON and toJSON functions (defaults to the identity mapper)
					objectMapping[key] = {
						jsonKey: fieldMapping.jsonKey || key,
						mapper: JSONMapper.create(fieldMapping.mapper) || JSONMapper.identity,
					};
				}
			});
			return objectMapping;
		}
	}
}



namespace JSONMapper {
	// Convenience definition for type assertions
	export type FromJSONFn<T> = (json: any) => T;
	export type ToJSONFn<T> = (value: T) => any;


	export interface JSONMapperLike<T> {
		/**
		 * Convert a value from its raw JSON representation. The raw representation will be a string,
		 * number, boolean, array, or plain object parsed from the JSON string.
		 */
		fromJSON?(json: any): T;

		/**
		 * Convert a value to its raw JSON representation. The return value can be a string, number,
		 * boolean, or any kind of object. Object (and array) values will be transformed recursively
		 * using the same rules as JSON.stringify before being formatted as a JSON string.
		 */
		toJSON?(value: T): any;
	}


	export function isJSONMapperLike(value: any): value is JSONMapperLike<any> {
		return (_.isFunction(value.toJSON) || _.isFunction(value.fromJSON));
	}


	export interface PropertyMapping<T> {
		jsonKey: string;
		mapper: JSONMapper<T>;
	}

	export interface ObjectMapping {
		[propertyKey: string]: PropertyMapping<any>;
	}

	export interface RawObjectMapping {
		[propertyKey: string]: true | string | JSONMapperLike<any> | PropertyMapping<any>;
	}
}

export = JSONMapper;
